/**
 * useXdcSupply — V2.K.9
 *
 * Fetches XDC supply data from CoinGecko.
 *
 * XDC Network (formerly XinFin Hybrid Blockchain) tokenomics :
 *   - **Pre-mine 37.5 B XDC** at mainnet genesis (May 30, 2019),
 *     held initially in a single genesis wallet then progressively
 *     distributed to the XinFin Foundation (~15 B), founders/team
 *     (~15 B), dApp dev (~10 B), and various pools.
 *   - **Ongoing inflation** : 5.5 XDC per ~2-second block → ~86.7 M
 *     XDC/year minted as Masternode rewards. There is **no hard cap
 *     post-pre-mine** — XDC is structurally inflationary, offset
 *     by fee burn.
 *   - **Fee burn** : 20 % of all smart-contract transaction fees are
 *     burned permanently.
 *   - Net inflation depends on transaction volume. As DeFi /
 *     enterprise adoption grows, the network can trend toward
 *     deflationary.
 *   - Consensus : **108 Masternodes** (Validator + Standby) on DPoS,
 *     min 10 M XDC stake. XDC 2.0 upgraded BFT + forensics.
 *
 * Refresh : every 5 minutes.
 */

import { useQuery } from '@tanstack/react-query';

export interface XdcSupplyData {
  circulating: number;
  total: number;
  /** Pre-mine reference value (genesis 30 May 2019). */
  genesisPremine: number;
  /** Annual mint at the current block reward + block time. */
  annualMint: number;
  /** Share of smart-contract transaction fees burned (constant). */
  feeBurnPct: number;
  /** Number of masternodes producing blocks (Validator + Standby). */
  masternodeSlots: number;
  /** Minimum XDC required to operate a Masternode. */
  masternodeMinStake: number;
  lastUpdated: string | null;
}

const XDC_GENESIS_PREMINE = 37_500_000_000;
const XDC_BLOCK_REWARD = 5.5;
const XDC_BLOCK_TIME_SECONDS = 2;
const SECONDS_PER_YEAR = 365.25 * 24 * 3600;
const XDC_ANNUAL_MINT = (XDC_BLOCK_REWARD * SECONDS_PER_YEAR) / XDC_BLOCK_TIME_SECONDS;
const XDC_FEE_BURN_PCT = 20;
const XDC_MASTERNODE_SLOTS = 108;
const XDC_MASTERNODE_MIN_STAKE = 10_000_000;

async function fetchXdcSupply(): Promise<XdcSupplyData> {
  const response = await fetch(
    'https://api.coingecko.com/api/v3/coins/xdce-crowd-sale?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false',
  );
  if (!response.ok) {
    throw new Error(`CoinGecko returned ${response.status}`);
  }
  const json = await response.json();
  const md = json.market_data || {};
  const circulating = Number(md.circulating_supply) || 0;
  const total = Number(md.total_supply) || circulating;

  return {
    circulating,
    total,
    genesisPremine: XDC_GENESIS_PREMINE,
    annualMint: XDC_ANNUAL_MINT,
    feeBurnPct: XDC_FEE_BURN_PCT,
    masternodeSlots: XDC_MASTERNODE_SLOTS,
    masternodeMinStake: XDC_MASTERNODE_MIN_STAKE,
    lastUpdated: json.last_updated || null,
  };
}

export function useXdcSupply() {
  return useQuery({
    queryKey: ['xdc', 'supply'],
    queryFn: fetchXdcSupply,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });
}
